import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Badge,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Input,
  Spinner,
  Label,
  Form
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { formatCreatedDate, workstatusOptions, groupedAssetStatusOptions } from '../constants/utils';
import { ConfirmDeleteModal, WorkOrderStatusBadge } from './Common';
import api from '../helpers/api';
import { translateActivity } from '../helpers/activityTranslator';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const PreventiveModal = ({ isOpen, toggle, workOrder, onRefresh }) => {
  const { t } = useTranslation();
  const user = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = user?.activeOrganizationId ?? null;

  const [activeTab, setActiveTab] = useState('details');
  const [editingSubWO, setEditingSubWO] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [subWOToDelete, setSubWOToDelete] = useState(null);

  // Animation variants
  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: -50
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 }
    }
  };


  const getFrequencyLabel = (frequency) => {
    const frequencyMap = {
      'DAILY': t('shared.daily', 'Daily'),
      'WEEKLY': t('shared.weekly', 'Weekly'),
      'MONTHLY': t('shared.monthly', 'Monthly'),
      'QUARTERLY': t('shared.quarterly', 'Quarterly'),
      'YEARLY': t('shared.yearly', 'Yearly')
    };
    return frequencyMap[frequency] || frequency;
  };

  const getCategoryLabel = (category) => {
    const categoryMap = {
      'TIME_BASED': t('shared.pm_category_time_based', 'Time Based'),
      'USAGE_BASED': t('shared.pm_category_usage_based', 'Usage Based'),
      'CONDITION_BASED': t('shared.pm_category_condition_based', 'Condition Based'),
      'SAFETY_COMPLIANCE': t('shared.pm_category_safety_compliance', 'Safety Compliance'),
      'DEFERRED': t('shared.deferred', 'Deferred')
    };
    return categoryMap[category] || category;
  };

  const getUsageStatusLabel = (status) => {
    if (!status) return t('shared.n_a', 'N/A');
    for (const group of groupedAssetStatusOptions(t)) {
      const option = group.options.find(opt => opt.value === status);
      if (option) return option.label;
    }
    return status;
  };

  // Fetch technicians when modal opens (uses RBAC user_roles table)
  useEffect(() => {
    const fetchTechnicians = async () => {
      if (!organizationId || !isOpen) return;
      try {
        const { data } = await api.get(`user/technicians`, {
          params: { organizationId: organizationId }
        });
        setTechnicians(data?.response || []);
      } catch (error) {
        console.error('Error fetching technicians:', error);
        setTechnicians([]);
      }
    };

    fetchTechnicians();
  }, [organizationId, isOpen]);

  // Fetch activities when activity tab is opened
  useEffect(() => {
    if (workOrder?.id && activeTab === 'activity') {
      getActivity();
    }
  }, [workOrder?.id, activeTab]);

  const getActivity = async () => {
    try {
      setLoadingActivities(true);
      const { data } = await api.get(`preventive/activity?parentId=${workOrder?.id}`);
      setActivities(data.response || []);
    } catch (error) {
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!commentText.trim()) {
      return;
    }

    try {
      setSubmittingComment(true);
      const payload = {
        parentId: workOrder?.id,
        activity: commentText.trim()
      };

      await api.patch('preventive/comment', payload);

      // Clear the comment input
      setCommentText('');

      // Refresh activities to show the new comment
      await getActivity();
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert(t('business.components.preventive_modal.comment_error', 'Failed to submit comment'));
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleCommentKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCommentSubmit(e);
    }
  };

  const formatDateForAPI = (date) => {
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseDateFromAPI = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  };

  const handleEditSubWO = (subWO) => {
    setEditingSubWO(subWO.id);
    setEditFormData({
      title: subWO.title || '',
      scheduledDate: parseDateFromAPI(subWO.scheduledDate),
      status: subWO.status,
      remarks: subWO.remarks || '',
      assignedTechnicianId: subWO.assignedTechnicianId || '',
      url: subWO.url || [],
      usageStatus: subWO.usageStatus || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingSubWO(null);
    setEditFormData({});
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingImages(true);
      const uploadedUrls = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append("files", file);

        const response = await api.post("files/img-upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        const publicUrl = response.data?.response[0]?.publicUrl;
        if (publicUrl) {
          uploadedUrls.push(publicUrl);
        }
      }

      setEditFormData(prev => ({
        ...prev,
        url: [...(prev.url || []), ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (indexToRemove) => {
    setEditFormData(prev => ({
      ...prev,
      url: (prev.url || []).filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSaveSubWO = async (subWO) => {
    try {
      setSaving(true);
      const payload = {
        id: subWO.id,
        title: editFormData.title,
        status: editFormData.status,
        remarks: editFormData.remarks,
        url: editFormData.url || [],
        usageStatus: editFormData.usageStatus || ''
      };

      // Only include assignedTechnicianId if status is PENDING and a technician is selected
      if (editFormData.status === 'PENDING' && editFormData.assignedTechnicianId) {
        payload.assignedTechnicianId = parseInt(editFormData.assignedTechnicianId);
      }

      await api.put('preventive/updateSubPreventive', payload);

      // Refresh the data to get updated work order
      if (onRefresh) {
        await onRefresh();
      }

      setEditingSubWO(null);
      setEditFormData({});
    } catch (error) {
      console.error('Error updating sub work order:', error);
      alert(t('business.components.preventive_modal.update_sub_wo_error', 'Failed to update sub work order'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (subWO) => {
    setSubWOToDelete(subWO);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!subWOToDelete) return;

    try {
      setDeleting(subWOToDelete.id);
      await api.delete(`preventive/deleteSubPreventive?id=${subWOToDelete.id}`);

      if (onRefresh) {
        await onRefresh();
      }

      setDeleteModalOpen(false);
      setSubWOToDelete(null);
    } catch (error) {
      console.error('Error deleting sub work order:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete sub work order';
      alert(errorMessage);
    } finally {
      setDeleting(null);
    }
  };

  const renderDetailsTab = () => (
    <div className="p-4">
      <Row className="g-4 mb-4">
        {/* Basic Information */}
        <Col md={6}>
          <motion.div variants={cardVariants} initial="hidden" animate="visible" className="h-100">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="info-icon me-3">
                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: '40px', height: '40px' }}>
                      <FeatherIcon icon="info" size={20} className="text-primary" />
                    </div>
                  </div>
                  <div>
                    <h6 className="mb-1 fw-bold">{t('shared.basic_information', 'Basic Information')}</h6>
                    <p className="text-muted small mb-0">{t('business.components.preventive_modal.details_subtitle', 'Work order details')}</p>
                  </div>
                </div>

                <div className="info-grid">
                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="hash" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.request_id', 'Request ID')}</span>
                    </div>
                    <p className="mb-0 text-dark">{workOrder.requestId || t('shared.n_a', 'N/A')}</p>
                  </div>

                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="calendar" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.created_date', 'Created Date')}</span>
                    </div>
                    <p className="mb-0 text-dark">{formatCreatedDate(workOrder.createdAt)}</p>
                  </div>

                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="calendar" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.start_date', 'Start Date')}</span>
                    </div>
                    <p className="mb-0 text-dark">{workOrder.startDate || t('shared.n_a', 'N/A')}</p>
                  </div>

                  <div className="info-item mb-0">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="calendar" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.end_date', 'End Date')}</span>
                    </div>
                    <p className="mb-0 text-dark">{workOrder.endDate || t('shared.n_a', 'N/A')}</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </motion.div>
        </Col>

        {/* PM Details */}
        <Col md={6}>
          <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }} className="h-100">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="info-icon me-3">
                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: '40px', height: '40px' }}>
                      <FeatherIcon icon="settings" size={20} className="text-success" />
                    </div>
                  </div>
                  <div>
                    <h6 className="mb-1 fw-bold">{t('shared.maintenance_details', 'Maintenance Details')}</h6>
                    <p className="text-muted small mb-0">{t('business.components.preventive_modal.pm_config_subtitle', 'PM configuration')}</p>
                  </div>
                </div>

                <div className="info-grid">
                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="tag" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.pm_category_label', 'PM Category')}</span>
                    </div>
                    <Badge color="info" style={{ fontSize: '12px', padding: '6px 12px' }}>
                      {getCategoryLabel(workOrder.pmCategory)}
                    </Badge>
                  </div>

                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="repeat" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.pm_frequency_label', 'PM Frequency')}</span>
                    </div>
                    <Badge color="primary" style={{ fontSize: '12px', padding: '6px 12px' }}>
                      {getFrequencyLabel(workOrder.pmFrequency)}
                    </Badge>
                  </div>

                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="layers" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.total_occurrences', 'Total Occurrences')}</span>
                    </div>
                    <Badge color="secondary">
                      {workOrder.occurrences || 0}
                    </Badge>
                  </div>

                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="check-circle" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.completed_occurrences', 'Completed Occurrences')}</span>
                    </div>
                    <Badge color="success">
                      {workOrder.completedOccurrences || 0}
                    </Badge>
                  </div>

                  <div className="info-item mb-0">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="activity" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.usage_status', 'Usage Status')}</span>
                    </div>
                    <Badge color="primary" style={{ fontSize: '12px', padding: '6px 12px' }}>
                      {getUsageStatusLabel(workOrder.usageStatus)}
                    </Badge>
                  </div>
                </div>
              </CardBody>
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* Asset Details Card */}
      <Row className="g-4">
        <Col md={12}>
          <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="info-icon me-3">
                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: '40px', height: '40px' }}>
                      <FeatherIcon icon="package" size={20} className="text-success" />
                    </div>
                  </div>
                  <div>
                    <h6 className="mb-1 fw-bold">{t('shared.asset_details', 'Asset Details')}</h6>
                    <p className="text-muted small mb-0">{t('shared.equipment_information', 'Equipment information')}</p>
                  </div>
                </div>

                <div className="info-grid">
                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="box" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.asset_name', 'Asset Name')}</span>
                    </div>
                    <p className="mb-0 text-dark">{workOrder?.assetId?.name || t('shared.not_specified', 'Not specified')}</p>
                  </div>

                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="layers" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.category', 'Category')}</span>
                    </div>
                    <p className="mb-0 text-dark">{workOrder?.assetId?.category || t('shared.not_specified', 'Not specified')}</p>
                  </div>

                  <div className="info-item mb-0">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="hash" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.serial_number', 'Serial Number')}</span>
                    </div>
                    <p className="mb-0 text-dark">{workOrder?.assetId?.serialNumber || t('shared.n_a', 'N/A')}</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </motion.div>
        </Col>
      </Row>

    </div>
  );


  const renderSubWorkOrdersTab = () => {
    const hasSubWorkOrders = workOrder.subWorkOrders && workOrder.subWorkOrders.length > 0;

    return (
      <div className="p-4">
        {hasSubWorkOrders ? (
          <Row className="g-4">
            <Col md={12}>
              <motion.div variants={cardVariants} initial="hidden" animate="visible">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                             style={{ width: '40px', height: '40px' }}>
                          <FeatherIcon icon="list" size={20} className="text-primary" />
                        </div>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">{t('shared.sub_work_orders', 'Sub Work Orders')}</h6>
                        <p className="text-muted small mb-0">
                          {t('business.components.preventive_modal.sub_wo_count', '{{count}} scheduled maintenance tasks', { count: workOrder.subWorkOrders.length })}
                        </p>
                      </div>
                    </div>

                    <div className="table-responsive">
                      <Table className="table table-hover mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>#</th>
                            <th>{t('shared.name', 'Name')}</th>
                            <th>{t('shared.title', 'Title')}</th>
                            <th>{t('shared.scheduled_date', 'Scheduled Date')}</th>
                            <th>{t('shared.status', 'Status')}</th>
                            <th>{t('shared.usage_status', 'Usage Status')}</th>
                            <th>{t('shared.technician', 'Technician')}</th>
                            <th>{t('shared.remarks', 'Remarks')}</th>
                            <th>{t('shared.images', 'Images')}</th>
                            <th>{t('shared.actions', 'Actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {workOrder.subWorkOrders.map((subWO, index) => (
                            <motion.tr
                              key={subWO.id || index}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                            >
                              <td>{index + 1}</td>
                              <td>
                                <span className="fw-medium">{subWO.name || t('shared.n_a', 'N/A')}</span>
                              </td>
                              {editingSubWO === subWO.id ? (
                                <>
                                  <td>
                                    <Input
                                      type="text"
                                      bsSize="sm"
                                      value={editFormData.title}
                                      onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                                      placeholder={t('shared.enter_title', 'Enter title')}
                                    />
                                  </td>
                                  <td>
                                    <small className="text-muted">{subWO.scheduledDate || t('shared.n_a', 'N/A')}</small>
                                  </td>
                                  <td>
                                    <Input
                                      type="select"
                                      bsSize="sm"
                                      value={editFormData.status}
                                      onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                                    >
                                      {workstatusOptions(t).map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </Input>
                                  </td>
                                  <td>
                                    <Input
                                      type="select"
                                      bsSize="sm"
                                      value={editFormData.usageStatus}
                                      onChange={(e) => setEditFormData({ ...editFormData, usageStatus: e.target.value })}
                                    >
                                      <option value="">{t('shared.select_status', 'Select Status')}</option>
                                      {groupedAssetStatusOptions(t).map((group) => (
                                        <optgroup key={group.group} label={group.group}>
                                          {group.options.map((status) => (
                                            <option key={status.value} value={status.value}>
                                              {status.label}
                                            </option>
                                          ))}
                                        </optgroup>
                                      ))}
                                    </Input>
                                  </td>
                                  <td>
                                    {editFormData.status === 'PENDING' ? (
                                      <Input
                                        type="select"
                                        bsSize="sm"
                                        value={editFormData.assignedTechnicianId}
                                        onChange={(e) => setEditFormData({ ...editFormData, assignedTechnicianId: e.target.value })}
                                      >
                                        <option value="">{t('shared.select_technician', 'Select Technician')}</option>
                                        {technicians.map(tech => (
                                          <option key={tech.id} value={tech.id}>{tech.name}</option>
                                        ))}
                                      </Input>
                                    ) : (
                                      <small className="text-muted">-</small>
                                    )}
                                  </td>
                                  <td>
                                    <Input
                                      type="text"
                                      bsSize="sm"
                                      value={editFormData.remarks}
                                      onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value })}
                                      placeholder={t('shared.add_remarks', 'Add remarks')}
                                    />
                                  </td>
                                  <td>
                                    <div>
                                      {editFormData.status === 'RESOLVED' && (
                                        <div className="mb-2">
                                          <Input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            bsSize="sm"
                                            multiple
                                            disabled={uploadingImages}
                                          />
                                          {uploadingImages && <Spinner size="sm" className="mt-1" />}
                                        </div>
                                      )}
                                      {editFormData.url && editFormData.url.length > 0 && (
                                        <div className="d-flex flex-wrap gap-1">
                                          {editFormData.url.map((imgUrl, imgIndex) => (
                                            <div key={imgIndex} className="position-relative" style={{ width: '50px', height: '50px' }}>
                                              <img
                                                src={imgUrl}
                                                alt={`Image ${imgIndex + 1}`}
                                                className="img-thumbnail"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                              />
                                              <button
                                                type="button"
                                                className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                                style={{ padding: '0px 4px', fontSize: '10px' }}
                                                onClick={() => removeImage(imgIndex)}
                                              >
                                                ×
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-1">
                                      <Button
                                        color="success"
                                        size="sm"
                                        onClick={() => handleSaveSubWO(subWO)}
                                        disabled={saving}
                                        title={t('shared.save', 'Save')}
                                      >
                                        {saving ? (
                                          <Spinner size="sm" />
                                        ) : (
                                          <FeatherIcon icon="check" size={14} />
                                        )}
                                      </Button>
                                      <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={handleCancelEdit}
                                        disabled={saving}
                                        title={t('shared.cancel', 'Cancel')}
                                      >
                                        <FeatherIcon icon="x" size={14} />
                                      </Button>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td>
                                    <small className="text-muted">{subWO.title || '-'}</small>
                                  </td>
                                  <td>{subWO.scheduledDate || t('shared.n_a', 'N/A')}</td>
                                  <td>
                                    <WorkOrderStatusBadge status={subWO.status} />
                                  </td>
                                  <td>
                                    {subWO.usageStatus ? (
                                      <Badge color="primary" style={{ fontSize: '11px', padding: '4px 8px' }}>
                                        {getUsageStatusLabel(subWO.usageStatus)}
                                      </Badge>
                                    ) : (
                                      <small className="text-muted">-</small>
                                    )}
                                  </td>
                                  <td>
                                    {subWO.assignedTechnicianId ? (
                                      <Badge color="primary" style={{ fontSize: '11px' }}>
                                        <FeatherIcon icon="user" size={10} className="me-1" />
                                        {typeof subWO.assignedTechnicianId === 'object'
                                          ? subWO.assignedTechnicianId.name
                                          : technicians.find(t => t.id === subWO.assignedTechnicianId)?.name || `ID: ${subWO.assignedTechnicianId}`}
                                      </Badge>
                                    ) : (
                                      <small className="text-muted">-</small>
                                    )}
                                  </td>
                                  <td>
                                    <small className="text-muted">
                                      {subWO.remarks || '-'}
                                    </small>
                                  </td>
                                  <td>
                                    {subWO.url && subWO.url.length > 0 ? (
                                      <div className="d-flex flex-wrap gap-1">
                                        {subWO.url.map((imgUrl, imgIndex) => (
                                          <a
                                            key={imgIndex}
                                            href={imgUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Click to view full image"
                                          >
                                            <img
                                              src={imgUrl}
                                              alt={`Image ${imgIndex + 1}`}
                                              className="img-thumbnail"
                                              style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'cover',
                                                cursor: 'pointer'
                                              }}
                                            />
                                          </a>
                                        ))}
                                      </div>
                                    ) : (
                                      <small className="text-muted">-</small>
                                    )}
                                  </td>
                                  <td>
                                    <div className="d-flex gap-1">
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handleEditSubWO(subWO)}
                                        disabled={editingSubWO !== null}
                                        title={t('shared.edit', 'Edit')}
                                      >
                                        <FeatherIcon icon="edit-2" size={14} />
                                      </Button>
                                      {subWO.status !== 'RESOLVED' && (
                                        <Button
                                          color="danger"
                                          size="sm"
                                          onClick={() => handleDeleteClick(subWO)}
                                          disabled={deleting === subWO.id || editingSubWO !== null}
                                          title={t('shared.delete', 'Delete')}
                                        >
                                          {deleting === subWO.id ? (
                                            <Spinner size="sm" />
                                          ) : (
                                            <FeatherIcon icon="trash-2" size={14} />
                                          )}
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </>
                              )}
                            </motion.tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            </Col>
          </Row>
        ) : (
          <div className="text-center py-5">
            <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                 style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
              <FeatherIcon icon="list" size={24} className="text-muted" />
            </div>
            <h6 className="text-muted">{t('business.components.preventive_modal.no_sub_wo_title', 'No Sub Work Orders')}</h6>
            <p className="text-muted mb-0">{t('business.components.preventive_modal.no_sub_wo_subtitle', "This work order doesn't have any sub work orders")}</p>
          </div>
        )}
      </div>
    );
  };

  const renderActivityTab = () => (
    <div className="p-4">
      <Card className="border-0 shadow-sm">
        <CardBody className="p-4">
          <div className="d-flex align-items-center mb-4">
            <div className="info-icon me-3">
              <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                   style={{ width: '40px', height: '40px' }}>
                <FeatherIcon icon="activity" size={20} className="text-info" />
              </div>
            </div>
            <div>
              <h5 className="mb-1 fw-bold">{t('shared.activity_timeline', 'Activity Timeline')}</h5>
              <p className="text-muted small mb-0">{t('business.components.preventive_modal.activity_subtitle', 'Complete history of work order activities')}</p>
            </div>
          </div>

          {loadingActivities ? (
            <div className="text-center py-5">
              <Spinner color="primary" size="lg" />
              <p className="text-muted mt-3">{t('shared.loading_activity_log', 'Loading activity log...')}</p>
            </div>
          ) : activities.length > 0 ? (
            <div className="activity-timeline" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {activities.map((activity, index) => (
                <motion.div
                  key={activity.id}
                  className="timeline-item d-flex mb-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="timeline-marker me-3">
                    <div className="timeline-dot bg-primary rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                      <FeatherIcon icon="clock" size={14} className="text-white" />
                    </div>
                  </div>
                  <div className="timeline-content flex-grow-1">
                    <div className="timeline-card bg-light rounded p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div className="fw-semibold text-dark">{activity.userName}</div>
                          <div className="text-muted">{translateActivity(activity, t)}</div>
                        </div>
                        <Badge color="light" className="text-muted small">
                          <FeatherIcon icon="calendar" size={12} className="me-1" />
                          {formatCreatedDate(activity?.createdAt)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                   style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                <FeatherIcon icon="clock" size={24} className="text-muted" />
              </div>
              <h5 className="text-muted">{t('shared.no_activity_recorded', 'No activity recorded')}</h5>
              <p className="text-muted mb-0">{t('business.components.preventive_modal.no_activity_recorded_subtitle', 'Activity will appear here as the work order progresses')}</p>
            </div>
          )}

          {/* Comment Section */}
          <div className="comment-section mt-4 pt-4 border-top">
            <div className="d-flex align-items-center mb-3">
              <div className="info-icon me-3">
                <div className="bg-secondary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                     style={{ width: '32px', height: '32px' }}>
                  <FeatherIcon icon="message-circle" size={16} className="text-secondary" />
                </div>
              </div>
              <h6 className="mb-0 fw-bold">{t('shared.add_comment', 'Add Comment')}</h6>
            </div>

            <Form onSubmit={handleCommentSubmit}>
              <div className="d-flex gap-3 align-items-start">
                <div className="comment-input flex-grow-1">
                  <Input
                    type="textarea"
                    placeholder={t('shared.add_comment_placeholder', 'Add a comment to this work order...')}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyPress={handleCommentKeyPress}
                    rows={3}
                    className="border-0 shadow-sm"
                    style={{
                      resize: 'none',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                      padding: '12px 16px'
                    }}
                    disabled={submittingComment}
                  />
                </div>
                <div className="comment-actions d-flex flex-column gap-2">
                  <Button
                    type="submit"
                    color="primary"
                    size="sm"
                    disabled={submittingComment || !commentText.trim()}
                    className="d-flex align-items-center gap-2"
                    style={{
                      borderRadius: '8px',
                      padding: '8px 16px'
                    }}
                  >
                    {submittingComment ? (
                      <>
                        <Spinner size="sm" />
                        <span>{t('shared.posting', 'Posting...')}</span>
                      </>
                    ) : (
                      <>
                        <FeatherIcon icon="send" size={14} />
                        <span>{t('shared.post', 'Post')}</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <small className="text-muted d-block mt-2">
                <FeatherIcon icon="info" size={12} className="me-1" />
                {t('shared.comment_help_text', 'Press Enter to post, Shift+Enter for new line')}
              </small>
            </Form>
          </div>
        </CardBody>
      </Card>
    </div>
  );

  // Early return if no work order - moved after all hooks
  if (!workOrder) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          toggle={toggle}
          size="xl"
          centered
          className="modern-modal"
          backdrop="static"
          style={{ maxHeight: '90vh' }}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="modal-content border-0 shadow-lg"
            style={{ borderRadius: '12px', overflow: 'hidden' }}
          >
            {/* Enhanced Header */}
            <div
              className="modal-header border-0 text-white position-sticky d-flex justify-content-between align-items-center"
              style={{
                top: 0,
                zIndex: 10,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '1.5rem'
              }}
            >
              <div className="d-flex align-items-center">
                <div className="ticket-icon me-3">
                  <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                       style={{ width: '48px', height: '48px' }}>
                    <FeatherIcon icon="tool" size={24} className="text-white" />
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h4 className="modal-title mb-1 text-white d-flex align-items-center">
                    <span className="me-2">{workOrder.title || t('business.components.preventive_modal.default_title', 'Preventive Maintenance Work Order')}</span>
                    <div className="d-flex align-items-center gap-2">
                      <WorkOrderStatusBadge status={workOrder.status} />
                    </div>
                  </h4>
                  <div className="d-flex align-items-center gap-3">
                    <span className="text-white small">
                      <FeatherIcon icon="hash" size={14} className="me-1" />
                      {workOrder.requestId}
                    </span>
                    <span className="text-white small">
                      <FeatherIcon icon="calendar" size={14} className="me-1" />
                      {formatCreatedDate(workOrder.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 ms-auto">
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={toggle}
                  aria-label={t('shared.close', 'Close')}
                ></button>
              </div>
            </div>

            {/* Enhanced Navigation */}
            <Nav
              tabs
              className="border-bottom-0 px-4 bg-light"
            >
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'details' ? 'active' : ''}`}
                  onClick={() => setActiveTab('details')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <FeatherIcon icon="file-text" size={16} className="me-2" />
                  {t('shared.details', 'Details')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'subWorkOrders' ? 'active' : ''}`}
                  onClick={() => setActiveTab('subWorkOrders')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <FeatherIcon icon="list" size={16} className="me-2" />
                  {t('shared.sub_work_orders', 'Sub Work Orders')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'activity' ? 'active' : ''}`}
                  onClick={() => setActiveTab('activity')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <FeatherIcon icon="activity" size={16} className="me-2" />
                  {t('shared.activity_log', 'Activity Log')}
                </NavLink>
              </NavItem>
            </Nav>

            {/* Modal Body */}
            <div
              className="modal-body p-0"
              style={{
                maxHeight: 'calc(90vh - 280px)',
                overflow: 'auto'
              }}
            >
              <TabContent activeTab={activeTab} className="h-100">
                <TabPane tabId="details" className="h-100">
                  {renderDetailsTab()}
                </TabPane>
                <TabPane tabId="subWorkOrders" className="h-100">
                  {renderSubWorkOrdersTab()}
                </TabPane>
                <TabPane tabId="activity" className="h-100">
                  {renderActivityTab()}
                </TabPane>
              </TabContent>
            </div>
          </motion.div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        message={t('business.components.preventive_modal.delete_sub_wo_confirm', 'Are you sure you want to delete sub work order "{{name}}"? This action cannot be undone.', { name: subWOToDelete?.name })}
      />
    </AnimatePresence>
  );
};

export default PreventiveModal;
